import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export function textOnlyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return { textOnly: true };
    }
    return null;
  };
}

/** Phone: digits only, exactly 11 digits, starts with 03 */
export function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value && value !== 0) return null;

    const strValue = String(value);

    if (!/^\d+$/.test(strValue)) {
      return { phoneSpecialChar: true };
    }
    if (!strValue.startsWith('03')) {
      return { phonePrefix: true };
    }
    if (strValue.length < 11) {
      return { phoneMinDigits: { min: 11, actual: strValue.length } };
    }
    if (strValue.length > 11) {
      return { phoneMaxDigits: { max: 11, actual: strValue.length } };
    }

    return null;
  };
}

/** Date must not be in the future (issue dates, DOB, etc.) */
export function noFutureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    const selected = new Date(value);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return selected > today ? { noFutureDate: true } : null;
  };
}

/** ✅ Date must be today or in the future — no past dates allowed */
export function noPastDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const selected = new Date(value);
    selected.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // selected < today → error (past dates blocked, today & future allowed)
    return selected < today ? { noPastDate: true } : null;
  };
}

/** ✅ Date must be in the future — no past, no current/today (for expiry dates) */
export function futureDateOnlyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const selected = new Date(value);
    selected.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // selected <= today → error (today bhi block, past bhi block)
    return selected <= today ? { futureDateOnly: true } : null;
  };
}

/** CNIC: digits only, exactly 13 digits */
/** CNIC: digits only, exactly 13 digits, must not start with 0 */
export function cnicValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value && value !== 0) return null;

    const strValue = String(value);

    // digits only
    if (!/^\d+$/.test(strValue)) {
      return { cnicSpecialChar: true };
    }

    // must not start with 0
    if (strValue.startsWith('0')) {
      return { cnicStartsWithZero: true };
    }

    // minimum length
    if (strValue.length < 13) {
      return {
        cnicMinDigits: {
          min: 13,
          actual: strValue.length,
        },
      };
    }

    // maximum length
    if (strValue.length > 13) {
      return {
        cnicMaxDigits: {
          max: 13,
          actual: strValue.length,
        },
      };
    }
    // must not be all identical digits (e.g. 1111111111111)
    if (/^(\d)\1+$/.test(strValue)) {
      return { cnicAllIdenticalDigits: true };
    }

    return null;
  };
}
/** ✅ Date of Birth: user must be at least 18 years old */
export function minAgeValidator(minAge: number = 18): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const dob = new Date(value);
    if (isNaN(dob.getTime())) {
      return { invalidDate: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // future DOB block
    if (dob > today) {
      return { dobFuture: true };
    }

    // calculate exact age
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();

    // adjust if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    if (age < minAge) {
      return { minAge: { requiredAge: minAge, actualAge: age } };
    }

    return null;
  };
}
/** 
 * Passport Validator (International Standard Friendly)
 
 */

export function passportValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) return null;

    const strValue = String(value).trim().toUpperCase();

    // ✅ Auto-uppercase: update control value if it differs
    if (control.value !== strValue) {
      control.setValue(strValue, { emitEvent: false });
    }

    // Only alphanumeric
    if (!/^[A-Z0-9]+$/.test(strValue)) {
      return { passportInvalidFormat: true };
    }

    // Length check
    if (strValue.length < 6) {
      return {
        passportMinLength: { min: 6, actual: strValue.length },
      };
    }

    if (strValue.length > 16) {
      return {
        passportMaxLength: { max: 16, actual: strValue.length },
      };
    }

    // Must contain at least 1 letter
    if (!/[A-Z]/.test(strValue)) {
      return { passportLetterRequired: true };
    }

    // Must contain at least 1 digit
    if (!/[0-9]/.test(strValue)) {
      return { passportNumberRequired: true };
    }

    return null;
  };
}
/** IBAN: alphanumeric only, exactly 24 characters */
export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const strValue = String(value).toUpperCase();

    if (!/^[A-Z0-9]+$/.test(strValue)) {
      return { ibanSpecialChar: true };
    }
    if (strValue.length < 24) {
      return { ibanMinLength: { min: 24, actual: strValue.length } };
    }
    if (strValue.length > 24) {
      return { ibanMaxLength: { max: 24, actual: strValue.length } };
    }

    return null;
  };

  
}
/**
 * Known email providers (yahoo, gmail, icloud) must use the ".com" TLD.
 * Any other domain (e.g. .org, .com.pk, custom company domains) is left
 * to basic email format validation and is not forced into ".com".
 */
export function knownEmailProviderValidator(): ValidatorFn {
  const knownProviders = ['yahoo', 'gmail', 'icloud'];

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const strValue = String(value).trim();
    const match = strValue.match(/^[^\s@]+@([^\s@]+)$/);
    if (!match) return null;

    const domain = match[1].toLowerCase();
    const host = domain.split('.')[0];

    if (knownProviders.includes(host) && domain !== `${host}.com`) {
      return { knownEmailProviderDomain: { host } };
    }

    return null;
  };
}

/** ✅ Numbers only — digits only, no letters, no special chars (no length limit) */
export function numbersOnlyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value && value !== 0) return null;

    const strValue = String(value);

    if (!/^\d+$/.test(strValue)) {
      return { numbersOnly: true };
    }
    return null;
  };
}

/** No leading/trailing spaces — value must equal its own trimmed form */
export function noEdgeSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const strValue = String(value);

    if (strValue !== strValue.trim()) {
      return { noEdgeSpaces: true };
    }
    return null;
  };
}

/** No uppercase letters allowed — alphabets must be lowercase only */
export function noUppercaseValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const strValue = String(value);

    if (/[A-Z]/.test(strValue)) {
      return { noUppercase: true };
    }
    return null;
  };
}

/** Digits must not all be identical (e.g. 0000000000, 4444444444) */
export function noIdenticalDigitsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value && value !== 0) return null;

    const strValue = String(value);

    if (/^(\d)\1+$/.test(strValue)) {
      return { identicalDigits: true };
    }
    return null;
  };
}

/** ✅ Alphanumeric only — letters + digits, no special chars, no spaces */
export function alphaNumericValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const strValue = String(value);

    if (!/^[a-zA-Z0-9]+$/.test(strValue)) {
      return { alphaNumeric: true };
    }
    return null;
  };


}
  /**
 * Non-negative decimal number.
 * Allows whole numbers and decimal values up to the provided decimal places.
 *
 * Examples:
 * 2500
 * 3.5
 * 3.50
 */
export function nonNegativeDecimalValidator(
  decimalPlaces: number = 2
): ValidatorFn {
  return (
    control: AbstractControl
  ): ValidationErrors | null => {
    const value = control.value;

    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const stringValue = String(value).trim();

    const pattern = new RegExp(
      `^\\d+(\\.\\d{1,${decimalPlaces}})?$`
    );

    if (!pattern.test(stringValue)) {
      return {
        nonNegativeDecimal: {
          decimalPlaces
        }
      };
    }

    return null;
  };
}